import {Formik, Form, Field, ErrorMessage} from 'formik';
import React, {useContext, useEffect, useState, useRef} from "react";
import {Modal} from 'reactstrap';
import CurrencyInput from 'react-currency-masked-input'
import './MakePayment.scss';
import StatusMessage from "../../components/statusMessage/StatusMessage";
import messages from "../../config/constant/messages";
import CalendarIcon from "../../assets/images/svgs/calendar-icon.svg";
import Calendar from 'react-calendar';
import Button from '../../components/button/Button';
import {useApolloClient, useLazyQuery, useMutation, useQuery} from "@apollo/client";
import query from "../../query";
import {Link} from "react-router-dom";
import PaymentDetailContext from "../../context/paymentDetailContext";
import Loader from "../../components/loader/Loader";
import moment from "moment";
import ContentBox from "../../components/contentBox/ContentBox";
import ReadOnlyInput from "../../components/readOnlyInput/ReadOnlyInput";
import useLoanId from "../../hooks/useLoanId";
import PaymentInfoBox from "./PaymentInfoBox";
import {PaymentConformation} from "./PaymentConformation";
import {parsePaymentAmount, paymentAmountsMatch} from "../../utils/parsePaymentAmount";

const MakePayment = () => {

  const {paymentDetail, updatePaymentData, message} = useContext(PaymentDetailContext);
  const loanId = useLoanId();
  const apolloClient = useApolloClient();

  const [loan, setLoan] = useState();
  const [paymentMethods, setPaymentMethods] = useState();
  const [activePaymentAmountBox, setActivePaymentAmountBox] = useState();
  const [activePaymentDateBox, setActivePaymentDateBox] = useState();
  const [formStep, setFormStep] = useState(0);
  const [isOpenDatePickerModal, openDatePickerModal] = useState(false);
  const [showAutopayWarning, setShowAutopayWarning] = useState(false);
  const [nextScheduledPayment, setNextScheduledPayment] = useState();
  const [cancelNextScheduledPaymentStatus, setCancelNextScheduledPaymentStatus] = useState(false);
  const [paymentError, setPaymentError] = useState();
  const [paymentData, setPaymentData] = useState();
  const [showCardFee, setShowCardFee] = useState(false);
  const [creditCardFee, setCreditCardFee] = useState();
  const [cardPromoEnabled, setCardPromoEnabled] = useState();
  const [creditCardPromoCoveredUntilAt, setCreditCardPromoCoveredUntilAt] = useState();
  const [isCreditCardPromoCovered, setCreditCardPromoCovered] = useState();
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState();
  const [currentPaymentMethodId, setCurrentPaymentMethodId] = useState();
  const [isApplePaySelected, setIsApplePaySelected] = useState(false);
  const [applePayToken, setApplePayToken] = useState(null);
  const [paymentPlanPreview, setPaymentPlanPreview] = useState(null);
  const [paymentPlanPreviewLoading, setPaymentPlanPreviewLoading] = useState(false);
  const [previewForAmount, setPreviewForAmount] = useState(null);
  const formikRef = useRef(null);
  const previewRequestIdRef = useRef(0);
  const latestAmountInputRef = useRef('');
  const [amountFieldValue, setAmountFieldValue] = useState('');
  const {loading, data: paymentDetailsData, refetch} = useQuery(query.getPaymentDetails, {
    variables: {
      loanId: loanId,
    },
  });

  const [getNextScheduledPayment, {data: loanNextScheduledPaymentsData}] = useLazyQuery(query.getNextScheduledPayment, {
    variables: {
      loanId: loanId,
    }
  })

  const [getCreditCardSettings, {data: creditCardSettingsData}] = useLazyQuery(query.getCreditCardSettings, {
    variables: {
      loanId: loanId,
    }
  })

  const syncAmountFieldValue = (rawValue) => {
    latestAmountInputRef.current = rawValue ?? '';
    setAmountFieldValue(rawValue ?? '');
  };

  const parsedAmountFromField = (rawValue) => parseAmountValue(rawValue ?? latestAmountInputRef.current);

  const [submitPayment, {
    data: lastPaymentData,
    loading: submitPaymentLoading,
    error: submitPaymentError
  }] = useMutation(query.submitPayment);

  const start = () => {
    setFormStep(0);
    refetch();
    getNextScheduledPayment({ variables: { loanId } });
    if (paymentDetailsData) {
      setLoan(paymentDetailsData['loans'][0]);
      setCreditCardFee(paymentDetailsData['loans'][0]['creditCardFee']);
      if (paymentDetailsData['paymentMethods'] && paymentDetailsData['paymentMethods'].length > 0) {
        setCardPromoEnabled(paymentDetailsData['paymentMethods'][0]['cardPromoEnabled']);
        setCreditCardPromoCoveredUntilAt(paymentDetailsData['paymentMethods'][0]['creditCardPromoCoveredUntilAt']);
      }
      setPaymentMethods(paymentDetailsData['paymentMethods']);
    }

    if (loanNextScheduledPaymentsData) {
      setNextScheduledPayment(loanNextScheduledPaymentsData.nextScheduledPayment);
    }
  };

  const configureCollectJS = (amount = 0) => {
    if (!window.CollectJS) {
      return;
    }
    
    window.CollectJS.configure({
      paymentSelector: '#demoPayButton',
      fields: {
        applePay: {
          selector: '.applePayButton',
          type: 'pay'
        }
      },
      price: amount.toFixed(2),
      currency: 'USD',
      country: 'US',
      callback: function (response) {
        if (response.token) {
          setApplePayToken(response.token);
          if (formikRef.current) {
            formikRef.current.setFieldValue('applePayToken', response.token);
            formikRef.current.submitForm();
          }
        } else if (response.error) {
          setPaymentError({message: 'Apple Pay payment failed. Please try again.'});
        }
      },
      timeoutCallback: () => {
        console.log('Apple Pay timeout or cancelled');
      },
    });
  }

  useEffect(() => {
    const checkCollectJS = setInterval(() => {
      if (window.CollectJS) {
        configureCollectJS(0);
        clearInterval(checkCollectJS);
      }
    }, 100);

    return () => clearInterval(checkCollectJS);
  }, []);

  useEffect(() => {
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    configureCollectJS();
  }, [paymentDetailsData, loanNextScheduledPaymentsData, paymentDetail]);

  useEffect(() => {
    const hasAutopay = paymentMethods?.some(paymentMethod => paymentMethod.autopay === true);
    const isUpcomingPayment = loan && isDateBeforeToday(loan['nextPaymentDate']);
    setShowAutopayWarning(Boolean(hasAutopay && isUpcomingPayment));
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan, paymentMethods])

  useEffect(() => {
    if (lastPaymentData) {
      setPaymentData(lastPaymentData['postPayment'])
      setPaymentError(undefined);
      setFormStep(1);
      if (lastPaymentData['postPayment']['paymentMethodId']) {
        const selectedPaymentMethod = paymentMethods.find(paymentMethod => paymentMethod['id'] == lastPaymentData['postPayment']['paymentMethodId'])
        setCurrentPaymentMethod(selectedPaymentMethod);
      }
    }
    if (submitPaymentError) {
      if(currentPaymentMethod && currentPaymentMethod['transactionType'] === 'creditcard'){
        setPaymentError({message: messages.paymentCreditCardError})
      }else{
        setPaymentError(submitPaymentError)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPaymentData, submitPaymentError]);

  useEffect(() => {
    if (creditCardSettingsData && isApplePaySelected && formikRef.current) {
      const settings = creditCardSettingsData.creditCardSettings;
      const values = formikRef.current.values;
      
      if (settings && values.amount && values.date) {
        const amount = parseFloat(values.amount.replace(/,/g, ''));
        calculateCreditCardFee(amount, values.date, settings, true);
      }
    }
  }, [creditCardSettingsData, isApplePaySelected]);

  const calculateCreditCardFee = (amount, paymentDate, settings, isApplePay = false) => {
    if (!settings || !amount || !paymentDate) return { fee: 0, isCovered: false };

    // Check promo coverage
    if (settings.cardPromoEnabled && settings.creditCardPromoCoverageUntil !== null) {
      const currentTimestamp = Math.floor(Date.now() / 1000);
      if (currentTimestamp <= settings.creditCardPromoCoverageUntil) {
        setCreditCardPromoCovered(true);
        setCreditCardFee(0);
        setShowCardFee(true);
        if (isApplePay) {
          configureCollectJS(amount + 0);
        }
        return { fee: 0, isCovered: true };
      }
    }
    
    setCreditCardPromoCovered(false);
    
    // Calculate fee
    const threshold = settings.cardPaymentThreshold || 0;
    const percentage = settings.cardPaymentProcessingPercentage || 0;
    const defaultFee = settings.cardPaymentDefaultFee || 0;
    
    const fee = amount > threshold 
      ? Number(((amount / 100.0) * percentage).toFixed(2))
      : Number(defaultFee.toFixed(2));
    
    setCreditCardFee(fee);
    setShowCardFee(true);
    
    if (isApplePay) {
      configureCollectJS(amount + fee);
    }
    
    return { fee, isCovered: false };
  }

  const creditCardCollection = () => paymentMethods && paymentMethods.filter(methods => methods.transactionType === 'creditcard').map(({id}) => id);

  const validatePaymentCardThreshold = (paymentMethod, amount) => {
    return amount > paymentMethod['cardPaymentThreshold'];
  }

  const getPaymentCardProcessingFee = (paymentMethod, amount) => {
    return Number(((amount/100) * paymentMethod['cardPaymentProcessingPercentage']).toFixed(2))
  }

  const calculateConvenienceFee = (paymentMethod, amount, paymentDateText) => {
    if(paymentMethod && amount){
      const creditCardIds = creditCardCollection();
      let parsedAmount = parsePaymentAmount(amount);
      if(!creditCardIds.includes(paymentMethod.id)){
        setCreditCardFee(0);
        setShowCardFee(false);
      }else{
        const [month, day, year] = paymentDateText.split('/').map(Number);
        const paymentDate = new Date(Date.UTC(year, month - 1, day));
        
        // Use unified fee calculation
        const settings = {
          cardPromoEnabled: cardPromoEnabled,
          creditCardPromoCoverageUntil: creditCardPromoCoveredUntilAt ? Math.floor(new Date(creditCardPromoCoveredUntilAt).getTime() / 1000) : null,
          cardPaymentThreshold: paymentMethod['cardPaymentThreshold'],
          cardPaymentProcessingPercentage: paymentMethod['cardPaymentProcessingPercentage'],
          cardPaymentDefaultFee: paymentMethod['cardPaymentDefaultFee']
        };
        
        calculateCreditCardFee(parsedAmount, paymentDateText, settings, false);
      }
    }
  }

  const parseAmountValue = parsePaymentAmount;

  const cancelInFlightPreview = () => {
    previewRequestIdRef.current += 1;
    setPaymentPlanPreviewLoading(false);
  };

  const clearPaymentPlanPreview = () => {
    cancelInFlightPreview();
    setPaymentPlanPreview(null);
    setPreviewForAmount(null);
  };

  const invalidatePreviewUnlessAmountMatches = (amountValue) => {
    if (previewForAmount === null && !paymentPlanPreview) {
      return;
    }

    if (!paymentAmountsMatch(amountValue, previewForAmount)) {
      clearPaymentPlanPreview();
    }
  };

  const normalizePaymentPlanPreview = (data) => {
    const preview = data?.chargedOffPaymentPlanAllocationPreview;
    if (!preview?.active) {
      return null;
    }

    if (preview.error) {
      return { error: preview.error };
    }

    return preview;
  };

  const fetchPaymentPlanPreview = (amount) => {
    const requestId = ++previewRequestIdRef.current;
    setPaymentPlanPreviewLoading(true);

    return apolloClient.query({
      query: query.previewChargedOffPaymentPlanAllocation,
      variables: {
        loanId: loanId,
        amount: amount
      },
      fetchPolicy: 'network-only',
    }).then(({ data }) => {
      if (requestId !== previewRequestIdRef.current) {
        return null;
      }

      const latestAmount = parsedAmountFromField(latestAmountInputRef.current);
      if (isNaN(latestAmount) || Math.abs(latestAmount - amount) > 0.005) {
        return null;
      }

      const preview = normalizePaymentPlanPreview(data);
      setPaymentPlanPreview(preview);
      setPreviewForAmount(amount);
      return preview;
    }).catch(() => {
      if (requestId !== previewRequestIdRef.current) {
        return null;
      }

      setPaymentPlanPreview(null);
      setPreviewForAmount(null);
      return null;
    }).finally(() => {
      if (requestId === previewRequestIdRef.current) {
        setPaymentPlanPreviewLoading(false);
      }
    });
  };

  const loadPaymentPlanPreviewForAmount = (amountValue) => {
    if (!loan?.hasActiveChargedOffPaymentPlan) {
      clearPaymentPlanPreview();
      return;
    }

    syncAmountFieldValue(amountValue);

    const amount = parsedAmountFromField(amountValue);
    if (isNaN(amount) || amount <= 0) {
      clearPaymentPlanPreview();
      return;
    }

    if (
      paymentPlanPreview &&
      previewForAmount !== null &&
      paymentAmountsMatch(amount, previewForAmount)
    ) {
      return;
    }

    clearPaymentPlanPreview();
    fetchPaymentPlanPreview(amount);
  };

  const fetchPaymentPlanPreviewOnCustomAmountBlur = (amountValue) => {
    loadPaymentPlanPreviewForAmount(amountValue);
  };

  const confirmPaymentPlanPreview = (preview) => {
    if (!preview?.message || !(preview.partial || preview.extraPayment)) {
      return true;
    }

    return window.confirm(`${preview.message}\n\nDo you want to continue with this payment?`);
  };

  const validateAmount = (value) => {
    let error;
    if (!value) {
      error = messages.required;
    } else {
      const amount = parseAmountValue(value);
      if (isNaN(amount) || amount <= 0) {
        error = 'Amount must be greater than 0';
      } else if (loan?.maxPaymentAmount === 0) {
        error = 'There is no payable balance on this account.';
      } else if (loan?.maxPaymentAmount > 0 && amount > loan.maxPaymentAmount) {
        error = `Amount cannot exceed $${loan.maxPaymentAmount.toFixed(2)}`;
      }
    }
    return error;
  }

  const validate = (value) => {
    let error;
    if (!value) {
      error = messages.required;
    }
    return error;
  }

  const isDateBefore = (date1, date2) => new Date(date1) > new Date(date2);
  const isDateBeforeToday = (date) => isDateBefore(date, new Date().toDateString());

  const currentFormAmount = amountFieldValue;
  const previewMatchesCurrentAmount = Boolean(
    paymentPlanPreview &&
    previewForAmount !== null &&
    paymentAmountsMatch(currentFormAmount, previewForAmount)
  );

  return <div className={'payment-form-container'}>
    <h3 className={'font-weight-bold'}>Provide payment information</h3>
    {nextScheduledPayment && (<Link to="/AccountSummary">
      <StatusMessage
        color={'danger'}
        value={messages.existedPaymentWarning}
        allowClose={true}
        className="d-flex"/>
    </Link>)}
    {showAutopayWarning && <StatusMessage
      color={'danger'}
      value={`You're currently setup on Autopay to be debited on ${loan['nextPaymentDate']}`}
      allowClose={true}
      className="d-flex"
    />}
    {paymentError && <StatusMessage
      color={'danger'}
      value={paymentError['message']}
      allowClose={true}
      className="d-flex"
    />}
    {loan?.hasActiveChargedOffPaymentPlan && <StatusMessage
      color={'info'}
      value={'This account has an active payment plan. Any payment you make, including extra payments, will be applied to your plan installments.'}
      allowClose={false}
      className="d-flex"
    />}
    {loan?.hasActiveChargedOffPaymentPlan && (
      <div className="payment-plan-allocation-preview-slot" aria-live="polite">
        {previewMatchesCurrentAmount && paymentPlanPreview?.error && (
          <StatusMessage
            color={'danger'}
            value={paymentPlanPreview.error}
            allowClose={true}
            className="d-flex"
          />
        )}
        {previewMatchesCurrentAmount && paymentPlanPreview?.message && (
          <StatusMessage
            color={'warning'}
            value={paymentPlanPreview.message}
            allowClose={false}
            className="d-flex"
          />
        )}
      </div>
    )}
    {message && <StatusMessage value={message} allowClose={true} className="d-flex"/>}
    {
      formStep === 1 ? <PaymentConformation paymentData={paymentData}
                                           currentPaymentMethod={currentPaymentMethod}
                                           cancelNextScheduledPaymentStatus={cancelNextScheduledPaymentStatus}
                                           setFormStep={() => setFormStep(0)}
                                           convenienceFeeCovered={(cardPromoEnabled && isCreditCardPromoCovered && currentPaymentMethod?.['transactionType'] === 'creditcard')}
        /> :
        <div className={'d-flex'}>
          {loading && 'Loading...'}
          {loan && paymentMethods &&
          <Formik
            innerRef={formikRef}
            initialValues={{
              loanId: parseInt(loanId),
              date: paymentDetail['date'] || '',
              amount: paymentDetail['amount'] || '',
              paymentMethodId: paymentDetail['paymentMethodId'] || '',
              cancelNextScheduledPayment: false,
              extraPayment: "false",
              applePayToken: ''
            }}
            onSubmit={async (values, {setSubmitting}) => {
              let amount = parseAmountValue(latestAmountInputRef.current || values.amount);

              if (loan?.hasActiveChargedOffPaymentPlan && !isNaN(amount) && amount > 0) {
                let preview = paymentPlanPreview;
                if (
                  paymentPlanPreviewLoading ||
                  !paymentAmountsMatch(amount, previewForAmount)
                ) {
                  preview = await fetchPaymentPlanPreview(amount);
                }

                if (preview?.error) {
                  setSubmitting(false);
                  return;
                }

                if (!confirmPaymentPlanPreview(preview)) {
                  setSubmitting(false);
                  return;
                }
              }

              let formattedValues = {
                ...values,
                amount: amount,
                cancelNextScheduledPayment: cancelNextScheduledPaymentStatus,
                extraPayment: values.extraPayment === 'true'
              }
              
              // Remove paymentMethodId from values when Apple Pay is selected
              if (isApplePaySelected) {
                delete formattedValues.paymentMethodId;
              } else {
                formattedValues.paymentMethodId = parseInt(values.paymentMethodId);
              }

              submitPayment({variables: formattedValues})
                .then(res => setPaymentData(res.postPayment)
                )
                .catch(err => setPaymentError(err))
            }}>
            {({values, setValues, errors, touched, isValid, isSubmitting, resetForm, dirty, setFieldValue}) => {

              const nextScheduledPaymentIsCancelled = () => {
                if (!nextScheduledPayment) return false;

                let amount = parsePaymentAmount(values.amount);
                let nextScheduledPaymentAmount = parsePaymentAmount(nextScheduledPayment['amount']);

                const today = moment();
                const paymentDate = moment(values.date);

                const isToday = today.isSame(paymentDate, 'day');
                const isAtLeastHalf = amount / nextScheduledPaymentAmount * 100 >= 50

                return isToday && isAtLeastHalf;
              }

              const paymentDate = values['date'];

              const handleAmountBoxClick = (boxName) => {
                let amount = loan[boxName].replace(/^\D+/g, '');
                let cancelNextScheduledPayment = nextScheduledPaymentIsCancelled();
                setCancelNextScheduledPaymentStatus(cancelNextScheduledPayment);
                setValues(prev => ({...prev, amount: amount, cancelNextScheduledPayment: cancelNextScheduledPayment}));
                syncAmountFieldValue(amount);
                invalidatePreviewUnlessAmountMatches(amount);
                calculateConvenienceFee(currentPaymentMethod, amount, paymentDate);
                setActivePaymentAmountBox(boxName);
              }

              const handlePaymentPlanInstallmentClick = () => {
                const amount = loan.chargedOffPaymentPlanNextInstallmentAmount?.toFixed(2) || '';
                let cancelNextScheduledPayment = nextScheduledPaymentIsCancelled();
                setCancelNextScheduledPaymentStatus(cancelNextScheduledPayment);
                setValues(prev => ({...prev, amount: amount, cancelNextScheduledPayment: cancelNextScheduledPayment}));
                calculateConvenienceFee(currentPaymentMethod, amount, paymentDate);
                setActivePaymentAmountBox('paymentPlanInstallment');
                loadPaymentPlanPreviewForAmount(amount);
              }

              const handleCustomAmountInput = () => {
                let cancelNextScheduledPayment = nextScheduledPaymentIsCancelled();
                setCancelNextScheduledPaymentStatus(cancelNextScheduledPayment);
                setValues(prev => ({...prev, cancelNextScheduledPayment: cancelNextScheduledPayment}))
                syncAmountFieldValue(values.amount || '');
                setActivePaymentAmountBox('customAmount');
              };

              const handleCustomAmountBlur = (form, field, rawValue) => {
                syncAmountFieldValue(rawValue);
                form.setFieldValue('amount', rawValue, false);

                if (activePaymentAmountBox !== 'customAmount') {
                  return;
                }

                fetchPaymentPlanPreviewOnCustomAmountBlur(rawValue);
              };

              const handleAmountInput = (form, value) =>{
                syncAmountFieldValue(value);
                form.setFieldValue('amount', value, false);
                invalidatePreviewUnlessAmountMatches(value);
                calculateConvenienceFee(currentPaymentMethod, value, paymentDate);

                // Recalculate Apple Pay fees if Apple Pay is selected
                if (isApplePaySelected && creditCardSettingsData && value && paymentDate) {
                  const settings = creditCardSettingsData.creditCardSettings;
                  const amount = parseAmountValue(value);
                  calculateCreditCardFee(amount, paymentDate, settings, true);
                }
              }

              const handleDateBoxClick = (boxName) => {
                // Prevent date changes when Apple Pay is selected
                if (isApplePaySelected) return;
                
                let cancelNextScheduledPayment = nextScheduledPaymentIsCancelled();
                setCancelNextScheduledPaymentStatus(cancelNextScheduledPayment);
                let date = loan[boxName] || moment(new Date()).format('MM/DD/YYYY');
                setValues(prev => ({...prev, date: date, cancelNextScheduledPayment: cancelNextScheduledPayment, extraPayment: "false"}));
                setActivePaymentDateBox(boxName);
              }

              const handlePaymentMethodChange = (event) => {
                let selectedPaymentMethodId = event.target.value
                setCurrentPaymentMethodId(selectedPaymentMethodId);
                const selectedPaymentMethod = paymentMethods.find(paymentMethod => paymentMethod['id'] === selectedPaymentMethodId)
                setCurrentPaymentMethod(selectedPaymentMethod);
                setValues(prev => ({...prev, paymentMethodId: selectedPaymentMethodId}));
                calculateConvenienceFee(selectedPaymentMethod, values.amount, paymentDate);
                setIsApplePaySelected(false);
              }

              const handleApplePayClick = () => {
                setIsApplePaySelected(true);
                setCurrentPaymentMethodId(null);
                setCurrentPaymentMethod(null);
                
                // Set payment date to today when Apple Pay is selected
                const todayDate = moment(new Date()).format('MM/DD/YYYY');
                setValues(prev => ({...prev, paymentMethodId: '', date: todayDate}));
                setActivePaymentDateBox('earliestPaymentDate');
                
                // Fetch credit card settings for Apple Pay
                if (loanId) {
                  getCreditCardSettings({ variables: { loanId } });
                }
                
                if (values.amount) {
                  const amount = parseFloat(values.amount.replace(/,/g, ''));
                  configureCollectJS(amount);
                }
              }

              const closeDatePicker = () => {
                setValues(prev => ({...prev, date: ''}));
                openDatePickerModal(false);
              }

              const resetFormState = () => {
                resetForm();
                setActivePaymentAmountBox(null);
                setActivePaymentDateBox(null);
                setIsApplePaySelected(false);
                setCurrentPaymentMethodId(null);
                setCurrentPaymentMethod(null);
                setShowCardFee(false);
                setCreditCardFee(0);
                clearPaymentPlanPreview();
                setPaymentPlanPreviewLoading(false);
                syncAmountFieldValue('');
              }

              return <Form>
                {submitPaymentLoading && <Loader/>}
                <Field type="hidden" name="applePayToken" />
                <div className={'my-4'}>
                  <div className={'d-flex align-items-center'}>
                    <div className="numberCircle m-2">1</div>
                    <h4 className={'mt-2'}>Select payment amount</h4>
                  </div>
                  <ErrorMessage name="amount" component="div" style={{color: 'red'}}/>
                  <div className="d-flex justify-content-center justify-content-md-start flex-wrap mb-3">
                    {loan && loan['daysPastDue'] > 0 &&
                    <ContentBox active={activePaymentAmountBox === 'pastDueAmount'} title={'Past Due Amount'}
                                footer={`${loan && loan['daysPastDue']} days past due`}
                                onClick={() => handleAmountBoxClick('pastDueAmount')}>
                      <ReadOnlyInput type="string" name="amount" value={loan && loan['pastDueAmount']}/>
                    </ContentBox>}
                    {loan && parseFloat(loan['currentDueBalance'].replace(/^\D+/g, '')) > 0 &&
                    <ContentBox active={activePaymentAmountBox === 'currentDueBalance'} title={'Total Amount Due'}
                                footer={`Due by ${loan && loan['nextPaymentDate']}`}
                                onClick={() => handleAmountBoxClick('currentDueBalance')}>
                      <ReadOnlyInput type="string" name="amount" value={loan && loan['currentDueBalance']}/>
                    </ContentBox>}
                    {loan?.hasActiveChargedOffPaymentPlan && loan?.chargedOffPaymentPlanNextInstallmentAmount > 0 &&
                    <ContentBox active={activePaymentAmountBox === 'paymentPlanInstallment'} title={'Payment Plan Installment'}
                                footer={'Next installment on payment plan'}
                                onClick={handlePaymentPlanInstallmentClick}>
                      <ReadOnlyInput type="string" name="amount"
                                     value={`$${loan.chargedOffPaymentPlanNextInstallmentAmount.toFixed(2)}`}/>
                    </ContentBox>}
                    <ContentBox active={activePaymentAmountBox === 'balance'} title={'Payoff Amount'}
                                onClick={() => handleAmountBoxClick('balance')}>
                      <ReadOnlyInput type="string" name="amount" value={loan && loan['balance']}/>
                    </ContentBox>
                    <ContentBox active={activePaymentAmountBox === 'customAmount'} title={'Enter amount'}
                                onClick={handleCustomAmountInput}>
                      <Field type="string" name={"amount"} validate={validateAmount}>
                        {({field, form}) => (
                          <CurrencyInput
                            name={field.name}
                            value={field.value ?? ''}
                            min="0"
                            onChange={(e, value) => {
                              handleAmountInput(form, value);
                            }}
                            onBlur={(e) => {
                              field.onBlur(e);
                              window.setTimeout(() => {
                                handleCustomAmountBlur(
                                  form,
                                  field,
                                  latestAmountInputRef.current || field.value || e.target.value
                                );
                              }, 0);
                            }}
                          />
                        )}
                      </Field>
                    </ContentBox>
                  </div>
                </div>
                <hr/>
                <div className={'my-4'}>
                  <div className={'d-flex align-items-center'}>
                    <div className="numberCircle m-2">2</div>
                    <h4 className={'mt-2'}>Select payment date</h4>
                  </div>
                  <ErrorMessage name="date" component="div" style={{color: 'red'}}/>
                  <div className="d-flex justify-content-center justify-content-md-start flex-wrap mb-3">
                    <ContentBox active={activePaymentDateBox == 'earliestPaymentDate'} title={'Earliest Payment Date'}
                                onClick={() => handleDateBoxClick('earliestPaymentDate')}
                                style={isApplePaySelected ? {opacity: 0.5, cursor: 'not-allowed'} : {}}>
                      <ReadOnlyInput type="string" name="date" value={moment().format('MMM Do YYYY')}/>
                    </ContentBox>
                    <ContentBox active={activePaymentDateBox == 'nextPaymentDate'} title={'Due Date'}
                                onClick={() => handleDateBoxClick('nextPaymentDate')}
                                style={isApplePaySelected ? {opacity: 0.5, cursor: 'not-allowed'} : {}}>
                      <ReadOnlyInput type="string" name="date"
                                     value={loan && moment(new Date(loan['nextPaymentDate'])).format('MMM Do YYYY')}/>
                    </ContentBox>
                    <ContentBox active={activePaymentDateBox == 'customPaymentDate'} title={'Enter Date'}
                                onClick={() => handleDateBoxClick('customPaymentDate')}
                                style={isApplePaySelected ? {opacity: 0.5, cursor: 'not-allowed'} : {}}>
                      <div className={'d-flex'}>
                        <Field type="string" name="date" validate={validate} style={{width: '100%'}} disabled={isApplePaySelected}/>
                        <div onClick={() => isApplePaySelected ? null : openDatePickerModal(true)}>
                          <div className={'m-1'}><img src={CalendarIcon} alt="calendar-icon"/></div>
                        </div>
                      </div>
                      {/* // Datepicker modal */}
                      <Modal isOpen={isOpenDatePickerModal} className="pick_your_date text-center d-flex">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title w-100 text-center" id="UpdateCustomerLabel">
                              Pick your Custom Date
                            </h5>
                          </div>
                          <div className="modal-body">
                            <div className="row justify-content-center">
                              <Calendar
                                value={new Date()}
                                onChange={(date) => {
                                  const formattedDate = moment(date).format('MM/DD/YYYY');
                                  setValues((prev) => ({
                                    ...prev,
                                    date: formattedDate
                                  }));

                                  calculateConvenienceFee(currentPaymentMethod, values.amount, formattedDate);
                                }}
                                minDate={new Date()}/>
                            </div>
                            <div className="modal-footer justify-content-center pb-0">
                              <Button
                                type="button"
                                data-dismiss="modal"
                                title="Cancel"
                                className="btn-custom-primary btn-custom-sm m-2 text-white"
                                onClick={closeDatePicker}
                              ></Button>
                              <Button
                                type="submit"
                                title="Submit"
                                hvrtitle="Submit Button"
                                className="btn btn-custom-primary alert-button text-white mt-0"
                                onClick={() => openDatePickerModal(prev => !prev)}
                              ></Button>
                            </div>
                          </div>
                        </div>
                      </Modal>
                    </ContentBox>
                  </div>
                </div>
                <hr/>
                <div className={'my-4'}>
                  <div className={'d-flex align-items-center'}>
                    <div className="numberCircle m-2">3</div>
                    <h4 className={'mt-2'}>Select payment method</h4>
                  </div>
                  <ErrorMessage name="paymentMethodId" component="div" style={{color: 'red'}}/>
                  <div className="d-flex justify-content-center justify-content-md-start flex-wrap">
                    <ContentBox title={'Payment methods'}>
                      <Field type="string"
                             name="paymentMethodId"
                             component='select'
                             validate={isApplePaySelected ? undefined : validate}
                             style={{width: '100%'}}
                             onChange={handlePaymentMethodChange}
                             value={currentPaymentMethodId || paymentDetail['paymentMethodId']}>
                        <option key={'default'} value={''}>Select method</option>
                        {paymentMethods && paymentMethods.map((paymentMethod, i) => <option key={i}
                                                                                            value={paymentMethod.id}>{paymentMethod.transactionType === 'creditcard' ? 'CARD' : 'ACH'} ****
                          **** {paymentMethod.lastFour}</option>)}
                      </Field>
                    </ContentBox>
                    <ContentBox active={isApplePaySelected} onClick={handleApplePayClick}>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 24 24" 
                          fill="currentColor"
                          style={{width: '24px', height: '24px'}}
                        >
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        <span style={{fontSize: '1.25rem'}}>Apple Pay</span>
                      </div>
                    </ContentBox>
                    <Link to='/PaymentMethods/AddingPaymentMethod'>
                      <ContentBox>
                        <button className={'btn btn-link'} onClick={() => updatePaymentData(values)}>Add Payment
                          Method
                        </button>
                      </ContentBox>
                    </Link>
                  </div>
                </div>
                {loan['daysPastDue'] === 0 && isDateBeforeToday(loan['nextPaymentDate']) && parseFloat(loan['currentDueBalance'].replace(/^\D+/g, '')) > 0 && !isDateBefore(values.date, loan['nextPaymentDate']) && <hr/>}
                {loan['daysPastDue'] === 0 && isDateBeforeToday(loan['nextPaymentDate']) && parseFloat(loan['currentDueBalance'].replace(/^\D+/g, '')) > 0 && !isDateBefore(values.date, loan['nextPaymentDate']) &&
                <div className={'my-4'}>
                  <div className={'d-flex align-items-center'}>
                    <div className="numberCircle m-2">4</div>
                    <h4 className={'mt-2'}>Select how apply the payment</h4>
                  </div>
                  <ErrorMessage name="extraPayment" component="div" style={{color: 'red'}}/>
                  <div className="d-flex justify-content-center justify-content-md-start flex-wrap">
                    <ContentBox title={'Options'} style={{width: '100%', height: '140px'}}>
                      <div role="group" aria-labelledby="extraPaymentGroup" style={{textAlign: 'left'}}>
                        <label>
                          <Field
                              name="extraPayment"
                              value="false"
                              type="radio"
                              checked={values.extraPayment === "false"}
                              onChange={() => setFieldValue("extraPayment", "false")}
                              style={{width: 'auto', marginRight: '3px'}}
                          />
                          Is this payment for {loan.nextPaymentDate} for the Amount of {loan.currentDueBalance}?
                        </label>
                        <br/>
                        <label>
                          <Field
                              name="extraPayment"
                              value="true"
                              type="radio"
                              checked={values.extraPayment === "true"}
                              onChange={() => setFieldValue("extraPayment", "true")}
                              style={{width: 'auto', marginRight: '3px'}}
                          />
                          Is this an extra payment and your automatic payment on {loan.nextPaymentDate} will still be processed for the Amount of {loan.currentDueBalance}?
                        </label>
                      </div>
                    </ContentBox>
                  </div>
                </div>}
                <hr/>
                {(isValid || (isApplePaySelected && validateAmount(values.amount) === undefined)) && dirty && (
                  <PaymentInfoBox
                    className="w-75"
                    paymentInfoValues={{
                      date: values.date,
                      amount: parseAmountValue(values.amount),
                      ...(cardPromoEnabled && isCreditCardPromoCovered && currentPaymentMethod?.['transactionType'] === 'creditcard'
                        ? {}
                        : { convenienceFee: `${showCardFee ? creditCardFee : 0}` }
                      )
                    }}
                    cancelNextScheduledPayment={cancelNextScheduledPaymentStatus}
                  />
                )}
                <div className={'d-flex mt-4 justify-content-sm-center justify-content-md-start'}>
                  {!isApplePaySelected && (
                    <>
                      <Button type="reset" onClick={() => resetFormState()}
                              className={'btn-custom-primary btn-custom-sm m-2 text-white'} title={'Cancel'}/>
                      <Button type="submit"
                              disabled={!isValid || !dirty || paymentPlanPreviewLoading || isSubmitting}
                              className={'alert-button m-2 text-white'}
                              title={'Submit'}/>
                    </>
                  )}
                  {isApplePaySelected && (
                    <div className="applePayButton m-2"></div>
                  )}
                  <div id="demoPayButton" style={{display: 'none'}}></div>
                </div>
              </Form>
            }}
          </Formik>}
        </div>}
  </div>
}

export default MakePayment;
